import React, { useState } from 'react';
import {
	CanvasEntityState,
	InputMapState,
	resolveEntityDataTypeLabel,
	resolveEntityName,
} from '@elyx-code/project-logic-tree';
import { useSearchStatementContext } from './search-statement-context';
import DataTypeIcon from '../../draggable-entity-card/DataTypeIcon';
import { Paper, IconButton } from '@mui/material';
import DropdownMenuPopup from '../../../lib/dropdown/DropdownMenuPopup';
import DynamicValueEntry from '../../dynamic-value/DynamicValueEntry';
import { Logger } from '@elyx-code/common-ts-utils';

const InlineInputMap = ({
	input,
	onEdit,
	onDelete,
}: {
	input: InputMapState;
	onEdit: () => void;
	onDelete: () => void;
}) => {
	const { project } = useSearchStatementContext();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	return (
		<>
			<Paper
				onDoubleClick={() => {
					onEdit();
				}}
				key={input.id}
				id={`inline-input-${input.id}`}
				elevation={3}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 4,
					padding: 8,
					minWidth: 170,
					// Don't shrink inside the horizontally-scrolling inputs row,
					// otherwise each card squeezes when the parent overflows
					// instead of staying its intended width and triggering scroll.
					flexShrink: 0,
				}}
			>
				<span
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<span
						style={{
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
							}}
						>
							<DataTypeIcon entity={input} size='small' />
							{resolveEntityName(input, project.logic)}
						</span>
						<p
							style={{
								margin: 0,
								marginLeft: 20,
								fontSize: 10,
								color: 'rgba(0,0,0,0.54)',
							}}
						>
							{resolveEntityDataTypeLabel(input)}
						</p>
					</span>

					{/* 3 dot menu */}
					<IconButton
						id={`inline-input-value-menu--${input.id}`}
						aria-label='input-value-menu'
						size='small'
						style={{ padding: 4, fontSize: 14, width: 22, height: 22 }}
						onClick={(e) => {
							// Open a menu with options to edit or delete the input value
							Logger.log('input value menu clicked: ', e);
							setIsMenuOpen(!isMenuOpen);
						}}
					>
						<i className='fa-solid fa-ellipsis-v' />
					</IconButton>
				</span>

				<DynamicValueEntry
					entity={input}
					project={project}
					parentDisabledReasons={[]}
				>
					{(extraContent) => (
						<div
							style={{
								marginLeft: 16,
							}}
						>
							{extraContent}
						</div>
					)}
				</DynamicValueEntry>
			</Paper>

			<DropdownMenuPopup
				popupOptions={{
					anchorQuerySelector: `#inline-input-value-menu--${input.id}`,
					placement: 'right',
					open: isMenuOpen,
				}}
				options={[
					{
						onClick: async () => {
							Logger.log('delete');
							await project.remove(input.id);
							onDelete?.();
						},
						disabled: !input.deletable,
						label: 'Delete',
						color: 'error',
						iconEnd: 'fa-solid fa-trash-can',
						tooltip: !input.deletable
							? 'This input cannot be deleted. Because the system need to refer to it over time.'
							: 'Click to delete this input',
					},
					{
						onClick: async () => {
							Logger.log('edit');
							onEdit?.();
						},
						disabled: false,
						label: 'Edit',
						iconEnd: 'fa-solid fa-pencil',
						tooltip: !input.editable ? 'This input has been locked and can no longer be edited' : 'Click to edit this input',
					},
				]}
			/>
		</>
	);
};

export default InlineInputMap;
